import { apiClient } from './client';
import type {
  WhiteLabel,
  CreateWhiteLabelData,
  UpdateWhiteLabelData,
  WhiteLabelResponse,
  SyncResponse,
} from '@/types/white-label';

export async function getWhiteLabels(): Promise<WhiteLabel[]> {
  const response = await apiClient.get<WhiteLabel[]>('/api/white-labels');
  return response.data;
}

export async function getWhiteLabel(id: number): Promise<WhiteLabel> {
  const response = await apiClient.get<WhiteLabel>(`/api/white-labels/${id}`);
  return response.data;
}

export async function createWhiteLabel(data: CreateWhiteLabelData): Promise<WhiteLabelResponse> {
  const response = await apiClient.post<WhiteLabelResponse>('/api/white-labels', data);
  return response.data;
}

export async function updateWhiteLabel(
  id: number,
  data: UpdateWhiteLabelData
): Promise<WhiteLabelResponse> {
  const response = await apiClient.put<WhiteLabelResponse>(`/api/white-labels/${id}`, data);
  return response.data;
}

export async function deleteWhiteLabel(id: number): Promise<{ message?: string; error?: string }> {
  const response = await apiClient.delete<{ message?: string; error?: string }>(`/api/white-labels/${id}`);
  return response.data;
}

export async function syncWhiteLabels(): Promise<SyncResponse> {
  const response = await apiClient.post<SyncResponse>('/api/white-labels/sync');
  return response.data;
}

// V2 Tenant Groups interface for deployment dialogs
export interface V2TenantGroupListResponse {
  data: Array<{
    id: string;
    name: string;
    host: string;
    status: 'ACTIVE' | 'INACTIVE' | 'SUSPENDED';
    tenantCount: number;
    createdAt: string | null;
  }>;
  defaultGroupId: string | null;
  warning?: string;
}

/**
 * Get all white labels including the default one (for V2 deployment)
 */
async function getWhiteLabelsWithDefault(): Promise<WhiteLabel[]> {
  const response = await apiClient.get<WhiteLabel[]>('/api/white-labels', {
    params: { includeDefault: 1 },
  });
  return response.data;
}

/**
 * Get V2 tenant groups for deployment selection
 * Uses white labels data to provide tenant group list
 */
export async function getV2TenantGroups(): Promise<V2TenantGroupListResponse> {
  const whiteLabels = await getWhiteLabelsWithDefault();

  // Transform white labels to V2 tenant groups format
  const data = whiteLabels
    .filter(wl => wl.v2TenantGroupId) // Only include white labels linked to V2
    .map(wl => ({
      id: wl.v2TenantGroupId!,
      name: wl.name,
      host: wl.v2Host || '',
      status: wl.v2Status || 'ACTIVE' as const,
      tenantCount: wl.tenantCount || 0,
      createdAt: wl.createdAt,
    }));

  // Find default group (École Futée)
  const defaultWhiteLabel = whiteLabels.find(wl => wl.isDefault);
  const defaultGroupId = defaultWhiteLabel?.v2TenantGroupId || data[0]?.id || null;

  return {
    data,
    defaultGroupId,
  };
}
